import { useQuery } from '@tanstack/react-query';
import type {
  ArtistAdminListItem,
  ArtistStatusEnum,
  ArtistTypeEnum,
} from '@/lib/api-admin/types';
import type { ApiMeta } from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import type { ArtistSort } from '@/stores/useArtistFiltersStore';

export interface ArtistsQueryParams {
  page?: number;
  size?: number;
  sort?: ArtistSort;
  type?: ArtistTypeEnum | null;
  status?: ReadonlyArray<ArtistStatusEnum>;
  vendorId?: string | null;
  keyword?: string;
  hasNoImage?: boolean;
}

export interface ArtistsPage {
  items: ReadonlyArray<ArtistAdminListItem>;
  meta: ApiMeta;
}

interface AdminQuery {
  page?: number;
  size?: number;
  sort?: ArtistSort;
  type?: ArtistTypeEnum;
  status?: ArtistStatusEnum[];
  vendorId?: string;
  keyword?: string;
  hasNoImage?: boolean;
}

const cleanQuery = (params: ArtistsQueryParams): AdminQuery => {
  const out: AdminQuery = {};
  if (params.page) out.page = Math.max(0, params.page - 1);
  if (params.size) out.size = params.size;
  // sort disabled (F-08): FE enum vs BE entity column mismatch; BE @PageableDefault 사용
  if (params.type) out.type = params.type;
  if (params.status && params.status.length > 0) out.status = [...params.status];
  if (params.vendorId) out.vendorId = params.vendorId;
  if (params.keyword && params.keyword.trim().length > 0) out.keyword = params.keyword.trim();
  if (params.hasNoImage) out.hasNoImage = true;
  return out;
};

export const artistsQueryKey = (params: ArtistsQueryParams) =>
  ['admin', 'artists', params] as const;

export const useArtistsQuery = (params: ArtistsQueryParams) =>
  useQuery<ArtistsPage>({
    queryKey: artistsQueryKey(params),
    queryFn: async () => {
      const [data, meta] = await apiClient.request('get', '/admin/v1/artists', {
        params: { query: cleanQuery(params) },
      });
      const payload = data as { items: ArtistAdminListItem[] };
      return { items: payload.items, meta };
    },
  });
